import { useState, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { EyeIcon, EyeOffIcon } from "lucide-react"
import { toast } from "sonner"
import { useAuth } from "@/contexts/auth-context"

const EMAIL_REGEX = /^(([^<>()[\].,;:\s@\"]+(\.[^<>()[\].,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\].,;:\s@\"]+\.)+[^<>()[\].,;:\s@\"]{2,})$/i

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login } = useAuth()
  const [email, setEmail] = useState(localStorage.getItem('email') || "")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (localStorage.getItem('rememberMe') === 'true' && EMAIL_REGEX.test(`${localStorage.getItem('email')}`)) {
      setRememberMe(true)
      setEmail(`${localStorage.getItem('email')}`)
    } else {
      setEmail('')
      setRememberMe(false)
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (email && password) {
      setLoading(true)

      try {
        const isLogged = await login(email, password)
        
        if (isLogged) {
          if (rememberMe) {
            localStorage.setItem('rememberMe', 'true')
            localStorage.setItem('email', email)
          } else {
            localStorage.setItem('rememberMe', '')
            localStorage.setItem('email', '')
          }
          
          // If the user was redirected here from a protected page (e.g. an MCP
          // integration deep-link), return them there after login. Falls back to
          // the last organization, then the org list.
          const from = (location.state as { from?: { pathname?: string; search?: string; hash?: string } } | null)?.from
          const lastOrg = localStorage.getItem('activeOrganization')
          console.log('Return-to:', from?.pathname, '| Last organization:', lastOrg)

          // Small delay to ensure token is saved
          setTimeout(() => {
            if (from?.pathname) {
              const dest = `${from.pathname}${from.search || ''}${from.hash || ''}`
              console.log('Returning to intended page:', dest)
              navigate(dest, { replace: true })
            } else if (lastOrg) {
              console.log('Navigating to:', `/${lastOrg}/optimizer`)
              navigate(`/${lastOrg}/optimizer`)
            } else {
              console.log('No last org, navigating to organizations')
              navigate('/organizations')
            }
          }, 100)
        } else {
          toast.error('Authentication Failed')
        }
      } catch (err: any) {
        toast.error(err?.response?.data?.error || 'Authentication Failed')
      } finally {
        setLoading(false)
      }
    } else {
      toast.error('Please enter email and password')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-[400px]">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl">Sign in</CardTitle>
          <CardDescription>
            Enter your email and password to sign in to your account
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="m@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? (
                    <EyeOffIcon className="h-4 w-4" />
                  ) : (
                    <EyeIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="remember"
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(checked as boolean)}
              />
              <Label
                htmlFor="remember"
                className="text-sm font-normal cursor-pointer"
              >
                Remember me
              </Label>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading ? "Signing in..." : "Sign in"}
            </Button>
            <div className="text-sm text-center space-y-1">
              <a href="/forgot-password" className="text-muted-foreground hover:text-primary">
                Forgot password?
              </a>
              <div>
                <span className="text-muted-foreground">Don't have an account? </span>
                <a href="/signup" className="text-primary hover:underline">
                  Sign up
                </a>
              </div>
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  )
}